/**
 * A conversation's newest page as loaded, merged with what the client already shows. Messages that changed
 * here while the page was loading (`changedMeanwhile`: a reply or delivery update that arrived, a send) keep
 * their state; those not on the page yet — arrived after it was read, or still being sent — stay at the end.
 *
 * When the page reaches back into what's shown, the earlier messages shown stay in front of it (`keptEarlier`), so
 * reading the page again (a reload) doesn't take away history the user loaded. When it doesn't, it replaces them:
 * more was written since than a page holds, and there'd be a gap. Only loaded history is kept (`cachedIsHistory`):
 * before a conversation's first page, what's cached is just messages that came in live, with gaps between them.
 */
export function mergeNewestChatPage(cached, page, changedMeanwhile, cachedIsHistory) {
    const cachedById = new Map(cached.map((message) => [message.id, message]));
    const onPage = new Set(page.map((message) => message.id));
    const firstOnPage = cached.findIndex((message) => onPage.has(message.id));
    const earlier = cachedIsHistory && firstOnPage > 0 ? cached.slice(0, firstOnPage) : [];
    const rest = firstOnPage > 0 ? cached.slice(firstOnPage) : cached;
    return {
        messages: [
            ...earlier,
            ...page.map((message) => (changedMeanwhile.has(message.id)
                ? cachedById.get(message.id) ?? message
                : message)),
            ...rest.filter((message) => !onPage.has(message.id) &&
                (changedMeanwhile.has(message.id) || message.status === 'pending')),
        ],
        keptEarlier: earlier.length > 0,
    };
}
